import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { doc, deleteField, onSnapshot, updateDoc } from 'firebase/firestore';
import {
  Paperclip,
  Send,
  FileText,
  X,
  Sparkles,
  Check,
  MessageSquareQuote,
  BookOpen,
  Smile,
  CornerUpLeft,
  Wand2,
  Undo2,
  Video,
  Minimize2,
  Maximize2,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { useResource } from '../hooks/useResource';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { api, directoryApi, uploadFile } from '../lib/api';
import { db } from '../lib/firebase';
import type { CannedResponse } from '../lib/types';
import { MOD_KEY, isModKey } from '../lib/platform';
import { useUiMode } from '../lib/uiMode';
import { FaqPanel } from './FaqPanel';
import { VideoPickerPanel } from './VideoPickerPanel';
import { EmojiPicker } from './EmojiPicker';

/** Bucket label for canned responses with no folder set. Sorts last. */
const UNFILED_FOLDER = 'Uncategorized';

export interface SuggestedReplyDoc {
  content: string;
  confidence?: number | null;
  generatedAt?: number;
  kbCitations?: string[];
  tone?: string;
}

export interface QuotedMessageInfo {
  id: string;
  content: string;
  type: 'customer' | 'agent' | 'ai' | 'system';
}

interface AgentRow {
  id: string;
  displayName?: string;
  email?: string;
}

export interface ComposerProps {
  tenantCode: string;
  chatId: string;
  agentUid?: string;
  onSend: (
    content: string,
    internal: boolean,
    attachment?: { path: string; mimeType: string; name?: string },
    quoted?: { quotedMessageId: string; quotedPreview: string },
    extras?: { subject?: string; usedDraft?: string },
  ) => Promise<void>;
  disabled?: boolean;
  suggestedReply?: SuggestedReplyDoc | null;
  suggestedReplies?: SuggestedReplyDoc[] | null;
  quotedMessage?: QuotedMessageInfo | null;
  onClearQuoted?: () => void;
  /** Chat channel — when 'email', the composer shows an editable subject. */
  channel?: string;
  /** Pre-filled subject. For the first reply this is `Re: <inboundSubject>`;
   *  subsequent replies pass the last sent subject. */
  defaultSubject?: string;
}

export interface ComposerHandle {
  /** Insert text into the draft: appends (with a newline) when non-empty,
   *  else replaces. Focuses the textarea afterwards. */
  insertText: (text: string) => void;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer({
  tenantCode,
  chatId,
  agentUid,
  onSend,
  disabled,
  suggestedReply,
  suggestedReplies,
  quotedMessage,
  onClearQuoted,
  channel,
  defaultSubject,
}: ComposerProps, ref) {
  // Simple view shows four actions — Internal note, Attach, Canned and Reply —
  // and folds FAQ / Video / emoji behind a "+" menu. Polish is not in that menu:
  // it appears inline the moment there is a draft to polish, so the tool people
  // actually like is one click away without holding a slot while it's a no-op.
  // Advanced renders every control inline, exactly as it always has.
  const { advanced } = useUiMode();
  const isEmail = channel === 'email';
  const [subject, setSubject] = useState<string>(defaultSubject ?? '');
  useEffect(() => {
    // Reset subject when switching chats or when the server-provided default
    // changes (e.g. inbound subject loaded after initial render).
    setSubject(defaultSubject ?? '');
  }, [chatId, defaultSubject]);
  const [content, setContent] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<{ name: string; path: string; mimeType: string } | null>(null);
  const [slashPickerOpen, setSlashPickerOpen] = useState(false);
  const [cannedMenuOpen, setCannedMenuOpen] = useState(false);
  const [cannedFilter, setCannedFilter] = useState('');
  const [faqOpen, setFaqOpen] = useState(false);
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // Simple view only: the "+" overflow that hosts FAQ / Video / emoji.
  const [moreOpen, setMoreOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [customerTyping, setCustomerTyping] = useState(false);
  const [aiReplying, setAiReplying] = useState(false);
  const [aiHandled, setAiHandled] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishError, setPolishError] = useState<string | null>(null);
  const [suggestionCollapsed, setSuggestionCollapsed] = useState(false);
  // "Try a different answer" — optional guidance box + loading state. The new
  // draft arrives via the live suggestedReply subscription, so no local result.
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenGuidance, setRegenGuidance] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  // Auto-expand when a fresh suggestion arrives so a collapsed box never hides
  // a new one.
  const firstSuggestionContent = suggestedReply?.content ?? suggestedReplies?.[0]?.content;
  useEffect(() => {
    setSuggestionCollapsed(false);
  }, [firstSuggestionContent]);
  const [prePolish, setPrePolish] = useState<string | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cannedMenuRef = useRef<HTMLDivElement | null>(null);
  const moreMenuRef = useOutsideClick<HTMLDivElement>(moreOpen, () => setMoreOpen(false));
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const typingActiveRef = useRef(false);
  const lastCustTypingRef = useRef(0);
  // The AI draft the agent inserted via "Use & edit" (null when they typed their
  // own reply). Sent as `usedDraft` so the server can capture the approve/correct
  // signal even though useSuggestion deletes the doc draft before send (F5).
  const usedDraftRef = useRef<string | null>(null);
  const custTypingHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAiTypingRef = useRef(0);
  const aiHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { items: canned } = useResource<CannedResponse>('canned_responses');
  const [agents, setAgents] = useState<AgentRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    directoryApi.listAgentsDirectory(tenantCode).then((r) => {
      if (!cancelled) setAgents(r.items as unknown as AgentRow[]);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  // Slash picker (existing behavior).
  const slashMatch = useMemo(() => {
    if (!slashPickerOpen) return null;
    const m = content.match(/^(\/[a-z0-9_-]*)$/i);
    if (!m) return null;
    const prefix = m[1].toLowerCase();
    return canned.filter((c) => c.shortcut.toLowerCase().startsWith(prefix)).slice(0, 6);
  }, [content, canned, slashPickerOpen]);

  // Mention picker (internal notes only): filter agents by name/email.
  const mentionMatches = useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery.toLowerCase();
    return agents
      .filter(
        (a) =>
          (a.displayName ?? '').toLowerCase().includes(q) ||
          (a.email ?? '').toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [agents, mentionOpen, mentionQuery]);

  function detectMention(next: string) {
    if (!internal) {
      setMentionOpen(false);
      return;
    }
    const ta = textareaRef.current;
    const caret = ta?.selectionStart ?? next.length;
    const upto = next.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\w.-]*)$/);
    if (m) {
      setMentionQuery(m[1] ?? '');
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  }

  function onContentChange(next: string, prev: string) {
    setContent(next);
    if (next.length > prev.length && next === '/' && prev === '') {
      setSlashPickerOpen(true);
    } else {
      if (next.length < prev.length) setSlashPickerOpen(false);
      if (!/^\/[a-z0-9_-]*$/i.test(next)) setSlashPickerOpen(false);
    }
    detectMention(next);
    scheduleTyping();
  }

  function applyCanned(c: CannedResponse) {
    setContent(c.content);
    setSlashPickerOpen(false);
    setCannedMenuOpen(false);
    setCannedFilter('');
  }

  function insertMention(a: AgentRow) {
    const ta = textareaRef.current;
    const caret = ta?.selectionStart ?? content.length;
    const upto = content.slice(0, caret);
    const after = content.slice(caret);
    const replaced = upto.replace(/(^|\s)@([\w.-]*)$/, (_, lead) => `${lead}@${a.displayName ?? a.email ?? a.id} `);
    setContent(replaced + after);
    setMentionOpen(false);
    setMentionQuery('');
    setTimeout(() => ta?.focus(), 0);
  }

  // Click-outside to close the canned menu.
  useEffect(() => {
    if (!cannedMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (cannedMenuRef.current && !cannedMenuRef.current.contains(e.target as Node)) {
        setCannedMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cannedMenuOpen]);

  const filteredCanned = useMemo(() => {
    const q = cannedFilter.trim().toLowerCase();
    if (!q) return canned;
    return canned.filter(
      (c) =>
        c.shortcut.toLowerCase().includes(q) ||
        (c.title ?? '').toLowerCase().includes(q) ||
        (c.folder ?? '').toLowerCase().includes(q) ||
        c.content.toLowerCase().includes(q),
    );
  }, [canned, cannedFilter]);

  // Group the filtered list by folder. Purely client-side over a collection the
  // onSnapshot already gave us — no extra fetch, no index. Folders sort A→Z
  // with the unfiled bucket pinned to the bottom.
  const cannedGroups = useMemo(() => {
    const byFolder = new Map<string, CannedResponse[]>();
    for (const c of filteredCanned) {
      const key = c.folder?.trim() || UNFILED_FOLDER;
      const bucket = byFolder.get(key);
      if (bucket) bucket.push(c);
      else byFolder.set(key, [c]);
    }
    return [...byFolder.entries()].sort(([a], [b]) =>
      a === UNFILED_FOLDER ? 1 : b === UNFILED_FOLDER ? -1 : a.localeCompare(b),
    );
  }, [filteredCanned]);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const path = await uploadFile(tenantCode, chatId, file);
      setPendingAttachment({ name: file.name, path, mimeType: file.type });
    } catch (err) {
      alert(`Upload failed: ${err instanceof Error ? err.message : err}`);
    } finally {
      setUploading(false);
    }
  }

  async function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
    if (fileRef.current) fileRef.current.value = '';
  }

  // Drag-and-drop: enable drop anywhere over the composer form. The thread
  // viewport relays drops via window-level listeners installed below.
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  // Allow drops onto the message-thread area too.
  useEffect(() => {
    const target = textareaRef.current?.closest('.flex.flex-col.min-w-0.min-h-0') as HTMLElement | null;
    if (!target) return;
    function over(e: DragEvent) {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      setIsDragging(true);
    }
    function leave(e: DragEvent) {
      if (e.target === target) setIsDragging(false);
    }
    function drop(e: DragEvent) {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      setIsDragging(false);
      const f = e.dataTransfer.files[0];
      if (f) void handleFile(f);
    }
    target.addEventListener('dragover', over);
    target.addEventListener('dragleave', leave);
    target.addEventListener('drop', drop);
    return () => {
      target.removeEventListener('dragover', over);
      target.removeEventListener('dragleave', leave);
      target.removeEventListener('drop', drop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  function scheduleTyping() {
    if (!agentUid) return;
    // Never start typing from a backgrounded tab.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (!typingActiveRef.current) {
        typingActiveRef.current = true;
        void api.setTyping(tenantCode, agentUid, chatId, true);
        // Heartbeat keeps the customer's indicator alive during a long reply;
        // bounded by the 3s idle stop below, so it never beats once the agent
        // pauses (or the tab is hidden) — a tab left open never keeps beating.
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          if (typingActiveRef.current && agentUid) {
            void api.setTyping(tenantCode, agentUid, chatId, true);
          }
        }, 3000);
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(stopTyping, 3000);
    }, 250);
  }

  function stopTyping() {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (typingActiveRef.current && agentUid) {
      typingActiveRef.current = false;
      void api.setTyping(tenantCode, agentUid, chatId, false);
    }
  }

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, []);

  // Subscribe to chat doc for the customer-typing and AI-replying indicators.
  // Both are driven off timestamp *transitions* + a client-relative hide timer
  // (skew-immune): never subtract the server-written timestamp from our own
  // clock. aiTypingAt is heartbeat'd by the AI runner while it generates a
  // reply, so the agent can see the customer is being handled right now.
  useEffect(() => {
    const applyTransition = (
      ts: number,
      lastRef: { current: number },
      hideRef: { current: ReturnType<typeof setTimeout> | null },
      setFlag: (v: boolean) => void,
    ) => {
      if (ts === 0) {
        if (hideRef.current) {
          clearTimeout(hideRef.current);
          hideRef.current = null;
        }
        lastRef.current = 0;
        setFlag(false);
      } else if (ts !== lastRef.current) {
        lastRef.current = ts;
        setFlag(true);
        if (hideRef.current) clearTimeout(hideRef.current);
        hideRef.current = setTimeout(() => {
          setFlag(false);
          lastRef.current = 0;
        }, 8000);
      }
    };
    const unsub = onSnapshot(doc(db, 'chats', chatId), (snap) => {
      const data = snap.data() as
        | { customerTypingAt?: number; aiTypingAt?: number; aiHandled?: boolean }
        | undefined;
      applyTransition(data?.customerTypingAt ?? 0, lastCustTypingRef, custTypingHideRef, setCustomerTyping);
      applyTransition(data?.aiTypingAt ?? 0, lastAiTypingRef, aiHideRef, setAiReplying);
      setAiHandled(data?.aiHandled === true);
    });
    return () => {
      unsub();
      if (custTypingHideRef.current) clearTimeout(custTypingHideRef.current);
      if (aiHideRef.current) clearTimeout(aiHideRef.current);
    };
  }, [chatId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    if (!content.trim() && !pendingAttachment) return;
    setSending(true);
    stopTyping();
    try {
      const text = content.trim();
      const quoted = quotedMessage
        ? {
            quotedMessageId: quotedMessage.id,
            quotedPreview: quotedMessage.content.slice(0, 100),
          }
        : undefined;
      const attachment = pendingAttachment
        ? {
            path: pendingAttachment.path,
            mimeType: pendingAttachment.mimeType,
            name: pendingAttachment.name,
          }
        : undefined;
      const usedDraft = !internal && usedDraftRef.current ? usedDraftRef.current : undefined;
      const extras =
        (isEmail && subject.trim().length > 0) || usedDraft
          ? { ...(isEmail && subject.trim().length > 0 ? { subject: subject.trim() } : {}), ...(usedDraft ? { usedDraft } : {}) }
          : undefined;
      await onSend(text, internal, attachment, quoted, extras);
      usedDraftRef.current = null;
      setContent('');
      setPendingAttachment(null);
      onClearQuoted?.();
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    if (disabled) {
      setContent('');
      setPendingAttachment(null);
    }
  }, [disabled]);

  async function dismissSuggestion() {
    try {
      await updateDoc(doc(db, 'chats', chatId), {
        suggestedReply: deleteField(),
        suggestedReplies: deleteField(),
      });
    } catch {
      // ignore
    }
  }

  async function useSuggestion(text?: string) {
    const next = text ?? suggestedReply?.content;
    if (!next) return;
    setContent(next);
    usedDraftRef.current = next; // remember what the AI proposed for F5 capture
    await dismissSuggestion();
  }

  // Approve-as-is: send the suggestion directly (graduated-autonomy review path)
  // without round-tripping through the content state. One keystroke (⌘/Ctrl+↵).
  async function approveSuggestion(text: string) {
    if (sending || !text.trim()) return;
    setSending(true);
    stopTyping();
    try {
      const extras = {
        ...(isEmail && subject.trim().length > 0 ? { subject: subject.trim() } : {}),
        usedDraft: text.trim(), // approve-as-is: the sent text IS the AI draft (F5)
      };
      await onSend(text.trim(), false, undefined, undefined, extras);
      usedDraftRef.current = null;
      setContent('');
      await dismissSuggestion();
    } finally {
      setSending(false);
    }
  }

  // Ask the AI for a different suggested reply, optionally steered by guidance.
  // The brain re-runs in suggest mode and overwrites suggestedReply server-side;
  // the new draft streams in via the live chat-doc subscription.
  async function regenerateSuggestion() {
    if (regenLoading) return;
    setRegenLoading(true);
    try {
      await api.regenerate(tenantCode, chatId, regenGuidance);
      setRegenOpen(false);
      setRegenGuidance('');
    } catch {
      // best effort; the card stays as-is if the re-suggest fails
    } finally {
      setRegenLoading(false);
    }
  }

  async function polishDraft() {
    const draft = content.trim();
    if (!draft || polishing) return;
    setPolishError(null);
    setPolishing(true);
    try {
      const locale = /[֐-׿]/.test(draft) ? 'he' : 'en';
      const r = await api.polish(draft, locale);
      if (r.text && r.text !== content) {
        setPrePolish(content);
        setContent(r.text);
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoTimerRef.current = setTimeout(() => setPrePolish(null), 8000);
      }
    } catch (e) {
      setPolishError(e instanceof Error ? e.message : 'Polish failed');
      setTimeout(() => setPolishError(null), 3000);
    } finally {
      setPolishing(false);
    }
  }

  function undoPolish() {
    if (prePolish == null) return;
    setContent(prePolish);
    setPrePolish(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }

  function insertEmoji(emoji: string) {
    const ta = textareaRef.current;
    if (!ta) {
      setContent((c) => c + emoji);
      return;
    }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const next = content.slice(0, start) + emoji + content.slice(end);
    setContent(next);
    setTimeout(() => {
      ta.focus();
      const pos = start + emoji.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }

  // Simple view hosts FAQ/Video/emoji behind one "+" button, so all three
  // panels share a single anchor and only one may be open at a time — two of
  // them open together would sit on top of each other.
  function openFromMore(which: 'faq' | 'video' | 'emoji') {
    setMoreOpen(false);
    setFaqOpen(which === 'faq');
    setVideoPickerOpen(which === 'video');
    setEmojiOpen(which === 'emoji');
  }

  // Imperative draft injection for "Use as reply" / "Reply" actions on a
  // message bubble (the thread can't reach the Composer's internal content state
  // otherwise). Appends with a blank line when the draft is non-empty.
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      setContent((c) => (c.trim() ? `${c}\n${text}` : text));
      setTimeout(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          const end = ta.value.length;
          ta.setSelectionRange(end, end);
        }
      }, 0);
    },
  }), []);

  // ---- Toolbar controls -------------------------------------------------
  // Defined once and placed by mode below. Only the TRIGGERS move between
  // modes (own button in advanced, "+" menu entry in simple), so each panel is
  // declared here and hosted by whichever wrapper is on screen.
  const faqPanel = faqOpen && (
    <FaqPanel
      tenantCode={tenantCode}
      onPaste={(answer) => setContent((c) => (c ? c + '\n\n' + answer : answer))}
      onClose={() => setFaqOpen(false)}
    />
  );
  const videoPanel = videoPickerOpen && (
    <VideoPickerPanel
      tenantCode={tenantCode}
      onPaste={(link) => setContent((c) => (c ? c + '\n' + link : link))}
      onClose={() => setVideoPickerOpen(false)}
    />
  );
  const emojiPanel = emojiOpen && (
    <EmojiPicker
      onPick={(e) => {
        insertEmoji(e);
        setEmojiOpen(false);
      }}
      onClose={() => setEmojiOpen(false)}
    />
  );

  const faqControl = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setFaqOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        title="Browse FAQs"
      >
        <BookOpen className="w-3.5 h-3.5" /> FAQ
      </button>
      {faqPanel}
    </div>
  );

  // Guide videos: drop the customer-facing link straight into the reply. The
  // full library lives at /guides.
  const videoControl = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setVideoPickerOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        title="Send a how-to video"
      >
        <Video className="w-3.5 h-3.5" /> Video
      </button>
      {videoPanel}
    </div>
  );

  const emojiControl = (
    <div className="relative">
      <button
        type="button"
        onClick={() => setEmojiOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        title="Insert emoji"
      >
        <Smile className="w-3.5 h-3.5" />
      </button>
      {emojiPanel}
    </div>
  );

  // Polish + the undo and the error that belong to it. They travel as one
  // cluster: a Polish the agent can't undo (or that fails silently) is worse
  // than no Polish at all.
  const polishControls = (
    <>
      <button
        type="button"
        onClick={polishDraft}
        disabled={disabled || polishing || !content.trim()}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-violet-700 bg-white border border-violet-300 rounded hover:bg-violet-50 disabled:opacity-50"
        title="Fix typos and grammar (AI)"
      >
        <Wand2 className="w-3.5 h-3.5" /> {polishing ? 'Polishing…' : 'Polish'}
      </button>
      {prePolish !== null && (
        <button
          type="button"
          onClick={undoPolish}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          title="Undo polish"
        >
          <Undo2 className="w-3.5 h-3.5" /> Undo
        </button>
      )}
      {polishError && <span className="text-[11px] text-red-600">{polishError}</span>}
    </>
  );

  const cannedControl = (
    <div className="relative" ref={cannedMenuRef}>
      <button
        type="button"
        onClick={() => setCannedMenuOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        title="Browse canned responses"
      >
        <MessageSquareQuote className="w-3.5 h-3.5" /> Canned ({canned.length})
      </button>
      {cannedMenuOpen && (
        <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg w-96 max-h-80 overflow-hidden flex flex-col z-20">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              placeholder="Filter canned responses…"
              value={cannedFilter}
              onChange={(e) => setCannedFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredCanned.length === 0 && (
              <div className="p-3 text-xs text-gray-500 text-center">No canned responses match.</div>
            )}
            {cannedGroups.map(([folder, rows]) => (
              <div key={folder}>
                {/* Sticky so the folder you're scrolling through stays
                    named at the top of the list. */}
                <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  {folder} <span className="font-normal normal-case text-gray-400">({rows.length})</span>
                </div>
                {rows.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => applyCanned(c)}
                    className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <strong className="text-primary-600 text-xs font-semibold">{c.shortcut}</strong>
                      {c.title && <span className="text-[11px] text-gray-500">{c.title}</span>}
                    </div>
                    <div className="text-[12px] text-gray-700 line-clamp-2" dir="auto">
                      {c.content}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Simple view only. Hosts the three folded-away triggers AND their panels, so
  // the panels stay anchored to the button that opened them.
  const moreControl = (
    <div className="relative" ref={moreMenuRef}>
      <button
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        title="More — FAQ, video, emoji"
        aria-label="More composer actions"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
      {moreOpen && (
        <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg w-44 py-1 z-20">
          <button
            type="button"
            onClick={() => openFromMore('faq')}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            <BookOpen className="w-3.5 h-3.5 text-gray-500" /> FAQ
          </button>
          <button
            type="button"
            onClick={() => openFromMore('video')}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            <Video className="w-3.5 h-3.5 text-gray-500" /> Video
          </button>
          <button
            type="button"
            onClick={() => openFromMore('emoji')}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            <Smile className="w-3.5 h-3.5 text-gray-500" /> Emoji
          </button>
        </div>
      )}
      {faqPanel}
      {videoPanel}
      {emojiPanel}
    </div>
  );

  return (
    <form
      onSubmit={submit}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setIsDragging(true);
        }
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      className={`relative border-t border-gray-200 p-3 flex-shrink-0 ${internal ? 'bg-amber-50' : 'bg-white'}`}
    >
      {isDragging && (
        <div className="absolute inset-0 z-30 bg-primary-50/80 border-2 border-dashed border-primary-400 rounded flex items-center justify-center text-primary-800 text-sm font-medium pointer-events-none">
          Drop file to attach
        </div>
      )}
      {customerTyping && (
        <div className="absolute -top-6 left-3 text-[11px] text-gray-500 flex items-center gap-1">
          <span>Customer is typing</span>
          <span className="inline-flex gap-0.5">
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" />
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '120ms' }} />
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '240ms' }} />
          </span>
        </div>
      )}
      {(() => {
        const cards = suggestedReplies && suggestedReplies.length > 0
          ? suggestedReplies
          : suggestedReply
            ? [suggestedReply]
            : [];
        if (cards.length === 0) return null;
        const conf = cards[0]?.confidence;
        // Below threshold → amber: the bot was unsure on this turn (graduated
        // autonomy held it for your review). A colour cue, not a new control.
        const lowConf = typeof conf === 'number' && conf < 0.6;
        const singleCard = cards.length === 1;
        return (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1 px-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-800 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                AI suggestion{cards.length > 1 ? 's' : ''}
                {typeof conf === 'number' && (
                  <span className={lowConf ? 'text-amber-700' : 'text-violet-800'}>
                    ({Math.round(conf * 100)}%)
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2">
                {singleCard && !content.trim() && !suggestionCollapsed && (
                  <span className="text-[10px] text-violet-500 hidden sm:inline">↵ insert · {MOD_KEY}+↵ send</span>
                )}
                <button
                  type="button"
                  onClick={() => setSuggestionCollapsed((v) => !v)}
                  className="text-violet-700 hover:text-violet-900"
                  title={suggestionCollapsed ? 'Expand suggestion' : 'Minimize suggestion'}
                >
                  {suggestionCollapsed ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={dismissSuggestion}
                  className="text-violet-700 hover:text-violet-900"
                  title="Dismiss all"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {suggestionCollapsed ? (
              <button
                type="button"
                onClick={() => setSuggestionCollapsed(false)}
                className="w-full text-left border-l-4 border-violet-500 bg-violet-50 hover:bg-violet-100 px-2.5 py-1 rounded text-xs text-violet-900 truncate transition-colors"
                title="Expand suggestion"
                dir="auto"
              >
                {cards[0]?.content?.replace(/\s+/g, ' ').trim() || 'AI suggestion'}
              </button>
            ) : (
            <div className={`grid gap-2 ${cards.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {cards.map((c, i) => (
                <div
                  key={i}
                  className="flex flex-col border-l-4 border-violet-500 bg-violet-50 rounded overflow-hidden"
                >
                  {c.tone && (
                    <div className="text-[10px] uppercase tracking-wide text-violet-700 px-2.5 pt-2.5 mb-0.5">
                      {c.tone}
                    </div>
                  )}
                  {/* Scrollable text region — long suggestions scroll instead of
                      truncating. Selecting/scrolling here must NOT fire the "use"
                      action (that lives in the footer button below). */}
                  <div
                    className="text-sm text-violet-950 whitespace-pre-wrap max-h-48 overflow-y-auto px-2.5 pt-2.5"
                    dir="auto"
                  >
                    {c.content}
                  </div>
                  {c.kbCitations && c.kbCitations.length > 0 && (
                    <div className="mt-1 px-2.5 text-[10px] text-violet-600 truncate" title={c.kbCitations.join(', ')}>
                      KB: {c.kbCitations.slice(0, 2).join(', ')}
                      {c.kbCitations.length > 2 ? ` +${c.kbCitations.length - 2}` : ''}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => useSuggestion(c.content)}
                    className="mt-2 mx-2.5 mb-2.5 inline-flex items-center justify-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-100 hover:bg-violet-200 rounded px-2 py-1 transition-colors"
                    title="Use this reply"
                  >
                    <Check className="w-3 h-3" /> Use {singleCard ? '& edit' : 'this'}
                  </button>
                </div>
              ))}
            </div>
            )}
            {!suggestionCollapsed && (
              <div className="mt-2">
                {regenOpen ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      value={regenGuidance}
                      onChange={(e) => setRegenGuidance(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); void regenerateSuggestion(); }
                        if (e.key === 'Escape') { setRegenOpen(false); setRegenGuidance(''); }
                      }}
                      placeholder="What should change? (optional)"
                      className="flex-1 text-xs border border-violet-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400"
                      dir="auto"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => void regenerateSuggestion()}
                      disabled={regenLoading}
                      className="text-[11px] font-medium text-white bg-violet-600 hover:bg-violet-700 rounded px-2 py-1 disabled:opacity-50"
                    >
                      {regenLoading ? '…' : 'Regenerate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRegenOpen(false); setRegenGuidance(''); }}
                      className="text-[11px] text-gray-500 hover:text-gray-700 px-1"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRegenOpen(true)}
                    disabled={regenLoading}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 hover:text-violet-900"
                    title="Ask the AI for a different answer (optionally with guidance)"
                  >
                    <RefreshCw className={`w-3 h-3 ${regenLoading ? 'animate-spin' : ''}`} /> Try a different answer
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })()}
      {quotedMessage && (
        <div className="mb-2 flex items-start gap-2 bg-gray-50 border-l-4 border-gray-400 rounded px-2 py-1.5">
          <CornerUpLeft className="w-3.5 h-3.5 text-gray-500 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
              Replying to {quotedMessage.type}
            </div>
            <div className="text-xs text-gray-700 truncate" dir="auto">
              {quotedMessage.content.slice(0, 140)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClearQuoted}
            className="text-gray-500 hover:text-gray-800"
            title="Cancel reply"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {slashMatch && slashMatch.length > 0 && (
        <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto z-10">
          {slashMatch.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => applyCanned(c)}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <strong className="text-primary-600">{c.shortcut}</strong>
              <span className="text-gray-500 ml-2">
                {c.content.slice(0, 80)}
                {c.content.length > 80 ? '…' : ''}
              </span>
            </button>
          ))}
        </div>
      )}
      {mentionOpen && mentionMatches.length > 0 && (
        <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto z-10">
          {mentionMatches.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => insertMention(a)}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <strong className="text-amber-700">@{a.displayName ?? a.email ?? a.id}</strong>
              {a.email && a.displayName && (
                <span className="text-gray-500 ml-2 text-xs">{a.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {isEmail && !internal && (
        <div className="mb-2 flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 shrink-0">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Re: …"
            disabled={disabled || sending}
            className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:border-violet-500 focus:outline-none disabled:opacity-50"
            dir="auto"
          />
        </div>
      )}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          dir="auto"
          onChange={(e) => onContentChange(e.target.value, content)}
          placeholder={
            internal
              ? 'Internal note (try @ to mention)…'
              : aiHandled
                ? 'AI is handling this chat — start typing to take over'
                : 'Reply to customer… (try /shortcut)'
          }
          rows={3}
          disabled={disabled || sending}
          onKeyDown={(e) => {
            // Graduated-autonomy review: empty draft + exactly one held
            // suggestion → ↵ inserts it for editing, ⌘/Ctrl+↵ approves & sends.
            const singleCard =
              suggestedReplies?.length === 1 || (!suggestedReplies?.length && !!suggestedReply);
            const cardText = suggestedReplies?.[0]?.content ?? suggestedReply?.content;
            if (!content.trim() && cardText && singleCard && e.key === 'Enter') {
              e.preventDefault();
              if (isModKey(e)) void approveSuggestion(cardText);
              else if (!e.shiftKey) void useSuggestion(cardText);
              return;
            }
            if (e.key === 'Enter' && isModKey(e)) submit(e);
            if (e.key === 'Escape') {
              setSlashPickerOpen(false);
              setCannedMenuOpen(false);
              setMentionOpen(false);
              setEmojiOpen(false);
              setMoreOpen(false); // simple view only; a no-op in advanced
            }
          }}
          className={`block w-full rounded-md border px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y [field-sizing:content] max-h-[45vh] ${
            !internal && aiHandled && !content.trim()
              ? 'bg-gray-100 border-gray-200 text-gray-500'
              : 'border-gray-300'
          }`}
        />
        {/* When the brain is generating, the agent doesn't need to reply — show
            a clear in-box notice (centered) and let them type to take over
            (overlay is click-through). */}
        {aiReplying && !internal && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-gray-50/85">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-700">
              <span>🤖 AI is replying…</span>
              <span className="inline-flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
            </span>
          </div>
        )}
      </div>
      {pendingAttachment && (
        <div className="mt-2 px-2 py-1.5 bg-primary-50 rounded text-xs flex items-center justify-between text-primary-900">
          <span className="flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> {pendingAttachment.name}
          </span>
          <button
            type="button"
            onClick={() => setPendingAttachment(null)}
            className="text-primary-700 hover:text-primary-900"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-y-2 justify-between items-center">
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center min-w-0">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
              className="w-3.5 h-3.5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            Internal note
          </label>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || uploading}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            <Paperclip className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Attach'}
          </button>
          <input
            ref={fileRef}
            type="file"
            onChange={pickFile}
            className="hidden"
            accept="image/*,application/pdf,text/*,audio/*,video/*"
          />
          {advanced ? (
            <>
              {faqControl}
              {videoControl}
              {polishControls}
              {emojiControl}
              {cannedControl}
            </>
          ) : (
            <>
              {cannedControl}
              {moreControl}
              {/* Contextual Polish: it earns its slot only when there is a draft
                  to run it on. Kept on screen while an undo or an error from
                  that run is still live, so neither can strand. */}
              {(content.trim() !== '' || prePolish !== null || polishError) && polishControls}
            </>
          )}
        </div>
        <button
          type="submit"
          disabled={disabled || sending || (!content.trim() && !pendingAttachment)}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white rounded shadow-sm transition-colors ml-auto ${
            internal ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary-600 hover:bg-primary-700'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Send className="w-3.5 h-3.5" />
          {sending ? 'Sending…' : internal ? 'Add note' : 'Reply'}
        </button>
      </div>
      <div className="mt-1.5 text-[11px] text-gray-400">
        {MOD_KEY}+Enter to send · type /shortcut for canned · @ to mention in notes
      </div>
    </form>
  );
});
